<script>
import cloneDeep from 'lodash/cloneDeep';
import AdminPanel from '@components/admin-panel/admin-panel.vue';
import CardWorkoutMinimal from '@components/cards/card-workout-minimal.vue';
import Layout from '@layouts/main.vue';
import SearchInput from '@components/search-input/search-input.vue';
import Sidebar from '@components/sidebar/sidebar.vue';
import SubheaderActions from '@components/subheader/subheader-actions.vue';
import UseLargeScreen from '@components/use-large-screen.vue';
import WorkoutForm from '@components/forms/workout-form.vue';
import { workoutModelMixin } from '@mixins/models/workout-model-mixin';
import { breakpointsMixin } from '@mixins/breakpoints-mixin.js';
import { helperMixin } from '@mixins/helper-mixin';
import {
  adminPanelMethods,
  authComputed,
  circuitComputed,
  circuitMethods,
  exerciseDifficultyComputed,
  exerciseDifficultyMethods,
  mediaComputed,
  mediaMethods,
  muscleGroupComputed,
  muscleGroupMethods,
  workoutComputed,
  workoutMethods,
  exerciseMethods,
  equipmentComputed,
  equipmentMethods,
} from '@state/helpers';

export default {
  page: {
    title: 'Workouts',
    meta: [{ name: 'description', content: 'The Workouts page.' }],
  },
  components: {
    AdminPanel,
    CardWorkoutMinimal,
    Layout,
    Sidebar,
    SearchInput,
    SubheaderActions,
    WorkoutForm,
    UseLargeScreen,
  },
  mixins: [workoutModelMixin, helperMixin, breakpointsMixin],
  data() {
    return {
      page: 1,
      morePages: true,
      initialWorkoutsList: null,
      selectedFilters: [],
      equipmentType: [],
      isLoadingWorkouts: false,
      searchComponentKey: 0,
      searchOpts: { keys: ['label'] },
      visibleOptsCount: 5,
      visibleEquipmentOptsCount: null,
      ownerWorkouts: [
        {
          label: 'My Library',
          id: this.trainerId,
        },
        {
          label: this.$store.getters['organization/organizationTitle'],
          id: '0',
        },
      ],
    };
  },
  computed: {
    ...authComputed,
    ...circuitComputed,
    ...exerciseDifficultyComputed,
    ...mediaComputed,
    ...muscleGroupComputed,
    ...workoutComputed,
    ...equipmentComputed,
    groupedWorkouts() {
      // TODO: Could probably thin this out...
      if (this.selectedFilters.length || this.equipmentType.length) {
        let workouts = [];
        if (this.isTrainer) {
          // We do owner filter if trainer otherwise don't bother
          const filters = this.selectedFilters;
          workouts = this.allWorkouts.filter((workout) => {
            if (
              filters.includes(this.trainerId) &&
              workout.trainer_id === this.trainerId
            ) {
              return workout;
            }
            if (filters.includes('0') && workout.trainer_id === '0') {
              return workout;
            }
            if (
              (filters.includes('0') && filters.includes(this.trainerId)) ||
              (!filters.includes(this.trainerId) && !filters.includes('0'))
            ) {
              return workout;
            }
          });
        } else {
          workouts = this.allWorkouts;
        }

        const filters = this.selectedFilters;
        const selectedTimes = filters.filter(
          (item) => typeof item === 'object'
        );

        if (this.muscleFilterSelected) {
          workouts = workouts.filter((workout) => {
            const hasMuscleGroups =
              workout.muscleGroups && workout.muscleGroups.length;
            if (hasMuscleGroups) {
              const matchesMuscleGroup = filters.some((filter) =>
                workout.muscleGroups.find(
                  (group) => filter === group.muscleGroup_id
                )
              );
              if (matchesMuscleGroup) {
                return workout;
              }
            }
          });
        }

        if (this.difficultyFilterSelected) {
          workouts = workouts.filter((workout) => {
            const difficulty =
              workout.exerciseDifficulty && workout.exerciseDifficulty.label
                ? workout.exerciseDifficulty.label.toLowerCase()
                : null;
            if (difficulty && filters.includes(difficulty)) {
              return workout;
            }
          });
        }

        if (selectedTimes && selectedTimes.length > 0) {
          workouts = workouts.filter((workout) => {
            return selectedTimes.find((time) => {
              if (
                workout.timeToComplete >= time.min &&
                workout.timeToComplete <= time.max
              ) {
                return workout;
              }
            });
          });
        }

        if (this.equipmentType.length > 0) {
          workouts = workouts.filter((workout) => {
            let matchingWorkout = null;
            if (workout.circuits && workout.circuits.length) {
              workout.circuits.forEach((circuit) => {
                if (circuit.exercises && circuit.exercises.length) {
                  circuit.exercises.forEach((exercise) => {
                    const equipments = this.requiredEquipment(exercise);
                    const hasEquipment = equipments && equipments.length;
                    if (hasEquipment) {
                      const matchEquipment = this.equipmentType.every(
                        (filter) =>
                          equipments.find(
                            (equipment) => filter === equipment._id
                          )
                      );
                      if (matchEquipment) {
                        matchingWorkout = workout;
                      }
                    } else {
                    }
                    if (
                      this.equipmentType &&
                      this.equipmentType.length &&
                      this.equipmentType.includes('bodyWeight')
                    ) {
                      const foundWeighted = this.equipmentType.every((filter) =>
                        this.allEquipment.find(
                          (equipment) =>
                            filter === equipment._id && equipment.isWeighted
                        )
                      );
                      if (!foundWeighted) {
                        matchingWorkout = workout;
                      }
                    }
                  });
                }
              });
            }
            if (matchingWorkout) {
              return matchingWorkout;
            }
          });
        }

        return workouts;
      } else {
        return this.allWorkouts;
      }
    },
    hasMuscleFilters() {
      return this.allMuscleGroups && this.allMuscleGroups.length;
    },
    muscleFilterSelected() {
      if (
        this.selectedFilters &&
        this.selectedFilters.length &&
        this.allMuscleGroups &&
        this.allMuscleGroups.length > 0
      ) {
        return this.selectedFilters.some((filter) =>
          this.allMuscleGroups.find((dfficulty) => filter === dfficulty._id)
        );
      } else {
        return false;
      }
    },
    difficultyFilterSelected() {
      if (
        this.selectedFilters &&
        this.selectedFilters.length &&
        this.hasDifficultyFilters
      ) {
        return this.selectedFilters.some((filter) =>
          this.allExerciseDifficulties.find(
            (difficulty) => filter === difficulty.label.toLowerCase()
          )
        );
      } else {
        return false;
      }
    },
    hasDifficultyFilters() {
      return (
        this.allExerciseDifficulties && this.allExerciseDifficulties.length
      );
    },
    isTrainer() {
      return (
        this.currentUser &&
        this.currentUser.roles &&
        this.currentUser.roles.isTrainer
      );
    },
    allEquipmentSorted() {
      const allEquipmentSortedd = [
        { _id: 'bodyWeight', label: 'Bodyweight' },
        ...this.allEquipment,
      ];
      const sortedArray = allEquipmentSortedd.sort((a, b) => {
        if (a.label < b.label) {
          return -1;
        }
        if (a.label > b.label) {
          return 1;
        }
        return 0;
      });
      return sortedArray;
    },
  },
  async created() {
    this.isLoadingWorkouts = true;
    await this.getWorkoutShape({
      orgId: this.userOrgId,
      trainerId: this.trainerId ? this.trainerId : '0',
    });

    do {
      this.page = this.page + 1;
      await this.getAllWorkouts({
        orgId: this.userOrgId,
        trainerId: this.trainerId ? this.trainerId : '0',
        page: this.page,
      })
        .then((res) => {
          this.morePages = res?.programWorkouts?.length > 0;
        })
        .catch((err) => {
          console.warn(err);
        });
    } while (this.morePages);
    this.isLoadingWorkouts = this.morePages;

    if (!this.allCircuits || !this.allCircuits.length) {
      this.getCircuitShape({
        orgId: this.userOrgId,
        trainerId: this.trainerId ? this.trainerId : '0',
      });
      this.getAllCircuits({
        orgId: this.userOrgId,
        trainerId: this.trainerId ? this.trainerId : '0',
      });
    }

    if (!this.allExercises || !this.allExercises.length) {
      this.getAllExerciseDifficulties({
        orgId: this.userOrgId,
        trainerId: this.trainerId ? this.trainerId : '0',
      });
      this.getAllMuscleGroups({
        orgId: this.userOrgId,
        trainerId: this.trainerId ? this.trainerId : '0',
      });
      this.getAllExercises({
        orgId: this.userOrgId,
        trainerId: this.trainerId ? this.trainerId : '0',
      });
    }

    if (!this.allEquipment || !this.allEquipment.length) {
      this.getAllEquipment({ orgId: this.userOrgId, trainerId: '0' });
    }

    this.initialWorkoutsList = cloneDeep(this.allWorkoutsTrueSource);
    this.ownerWorkouts[0].id = this.trainerId;
  },
  async mounted() {
    if (this.workoutFilter && this.workoutFilter.selectedFilters) {
      this.selectedFilters = this.workoutFilter.selectedFilters;
    }
    if (this.workoutFilter && this.workoutFilter.equipmentFilters) {
      this.equipmentType = this.workoutFilter.equipmentFilters;
    }
    this.visibleEquipmentOptsCount = this.visibleOptsCount;
  },
  destroyed() {
    this.setAllWorkouts([]);
    if (this.tempPhotoPreview) {
      this.setTempPhotoPreview(null);
    }
  },
  methods: {
    ...adminPanelMethods,
    ...circuitMethods,
    ...exerciseMethods,
    ...exerciseDifficultyMethods,
    ...mediaMethods,
    ...muscleGroupMethods,
    ...workoutMethods,
    ...equipmentMethods,
    onHidePanel() {
      if (this.hasUnsavedChanges) {
        this.$router.app.$bvModal.show('unsaved-changes-modal');
      }
    },
    showAdminPanel() {
      this.setShowAdminPanel();
    },
    onSelectFilter(val) {
      if (val && val.range) {
        if (this.selectedFilters.includes(val.range)) {
          this.selectedFilters = this.selectedFilters.filter(
            (item) => item !== val.range
          );
        } else {
          this.selectedFilters.push(val.range);
        }
      } else {
        if (this.selectedFilters.includes(val)) {
          this.selectedFilters = this.selectedFilters.filter(
            (item) => item !== val
          );
        } else {
          this.selectedFilters.push(val);
        }
      }
    },
    onFilterResults(val) {
      this.setAllWorkouts(val);
    },
    clearFilters() {
      this.selectedFilters = [];
      this.setAllWorkouts(this.initialWorkoutsList);
      const inputWithLabel = document.getElementsByClassName(
        'input-icon-wrapper'
      )[0];
      if (inputWithLabel) {
        if (inputWithLabel.children.length > 1) {
          const inputElement = inputWithLabel.children[1].firstChild;
          inputElement.value = null;
        }
      }
      if (this.$refs) {
        this.$refs.checkBox.forEach((checkBox) => {
          checkBox.$el.children[0].firstChild.checked = false;
        });
      }
    },
    visibilityChanged(isVisible, entry) {
      // Re-render search conponent when visibility changes on the search input parent
      this.searchComponentKey++;
    },
    onSelectEquipmentFilter(val) {
      if (this.equipmentType.includes(val)) {
        const index = this.equipmentType.findIndex((item) => item === val);
        if (index > -1) {
          this.equipmentType.splice(index, 1);
        }
      } else {
        this.equipmentType.push(val);
      }
    },
    requiredEquipment(exercise) {
      const listOfEquipment = [];
      if (exercise && exercise.requiredEquipment) {
        if (exercise.requiredEquipment.weighted) {
          listOfEquipment.push(exercise.requiredEquipment.weighted);
        }
        if (
          exercise.requiredEquipment.unweighted &&
          exercise.requiredEquipment.unweighted.length
        ) {
          exercise.requiredEquipment.unweighted.forEach((equipment) => {
            listOfEquipment.push(equipment);
          });
        }
      }
      return listOfEquipment;
    },
    optsCount(group) {
      switch (group) {
        case 'all':
          return this.allEquipment
            ? Object.keys(this.allEquipment).length + 1
            : 0;
        default:
          break;
      }
    },
    showAllOpts(group) {
      switch (group) {
        case 'all':
          this.visibleEquipmentOptsCount === this.visibleOptsCount
            ? (this.visibleEquipmentOptsCount = this.optsCount(group))
            : (this.visibleEquipmentOptsCount = this.visibleOptsCount);
          break;
        default:
          break;
      }
    },
  },
  watch: {
    equipmentType(newVal, oldVal) {
      const filter = {
        selectedFilters: this.selectedFilters,
        equipmentFilters: newVal,
      };
      this.setWorkoutFilter(filter);
    },
    selectedFilters(newVal, oldVal) {
      const filter = {
        selectedFilters: newVal,
        equipmentFilters: this.equipmentType,
      };
      this.setWorkoutFilter(filter);
    },
  },
};
</script>

<template>
  <UseLargeScreen v-if="isSmallScreenView" />
  <Layout v-else>
    <AdminPanel @hide-panel="onHidePanel" title="Create New Workout">
      <WorkoutForm />
    </AdminPanel>

    <div
      class="container-wrapper dark"
      v-observe-visibility="visibilityChanged"
    >
      <Sidebar type="light">
        <h5 class="sidebar-section-title">Filter Workouts</h5>

        <div class="sidebar-section" v-if="isTrainer">
          <div class="sidebar-section-container">
            <legend class="sidebar-subtitle">Library</legend>
            <div class="sidebar-section-container">
              <BaseCheckboxModel
                ref="owner_checkBox"
                itemRef="ownerWorkouts"
                v-for="(item, i) in ownerWorkouts"
                :item="item"
                :itemIndex="i"
                :key="i"
                :value="item.id"
                v-model="selectedFilters"
              />
            </div>
          </div>
        </div>
        <div v-if="hasMuscleFilters" class="sidebar-section">
          <div class="sidebar-section-container">
            <legend class="sidebar-subtitle">Muscle Group</legend>
            <div class="sidebar-section-container">
              <BaseCheckboxModel
                ref="muscleGroup_checkBox"
                itemRef="muscleGroup"
                v-for="(item, i) in allMuscleGroups"
                :item="item"
                :itemIndex="i"
                :key="i"
                :value="item._id"
                v-model="selectedFilters"
              />
            </div>
          </div>
        </div>
        <div v-if="allEquipment.length" class="sidebar-section">
          <div class="sidebar-section-container">
            <legend class="sidebar-subtitle">Equipment</legend>
            <div class="sidebar-section-container">
              <BaseCheckboxModel
                ref="equipment_checkBox"
                itemRef="equipment"
                v-for="(item, i) in allEquipmentSorted"
                :item="item"
                :itemIndex="i"
                :key="i"
                :value="item._id"
                v-model="equipmentType"
                :style="
                  `display: ${
                    parseInt(i) + 1 > visibleEquipmentOptsCount
                      ? 'none'
                      : 'inline-flex'
                  }`
                "
              />
            </div>
            <a
              class="link-more"
              v-if="optsCount('all') > visibleOptsCount"
              @click="showAllOpts('all')"
            >
              <span v-if="visibleEquipmentOptsCount === visibleOptsCount"
                >More...</span
              >
              <span v-else>Less...</span>
            </a>
          </div>
        </div>

        <div class="sidebar-section" v-if="hasDifficultyFilters">
          <div class="sidebar-section-container">
            <legend class="sidebar-subtitle">Level</legend>
            <div class="sidebar-section-container">
              <BaseCheckboxModel
                ref="checkBox"
                itemRef="exerciseDifficulty"
                v-for="(item, i) in allExerciseDifficulties"
                :item="item"
                :itemIndex="i"
                :key="i"
                :value="item.label"
                v-model="selectedFilters"
              />
            </div>
          </div>
        </div>

        <div class="sidebar-section">
          <div class="sidebar-section-container">
            <legend class="sidebar-subtitle">Time Required</legend>
            <div class="sidebar-section-container">
              <BaseCheckboxModel
                ref="checkBox"
                itemRef="timerOption"
                v-for="(item, i) in timeFilterOpts"
                :item="item"
                :itemIndex="i"
                :key="i"
                :value="item.label"
                v-model="selectedFilters"
              />
            </div>
          </div>
        </div>
      </Sidebar>

      <div class="content-wrapper">
        <SubheaderActions>
          <SearchInput
            :key="searchComponentKey"
            :list="initialWorkoutsList"
            :options="searchOpts"
            @filter-results="onFilterResults"
          />
          <BaseButton
            type="button"
            class="btn-primary btn-round"
            @click="showAdminPanel"
          >
            <img src="@assets/images/icon-powerlifter.svg" />
            Create New Workout
          </BaseButton>
        </SubheaderActions>

        <div v-if="groupedWorkouts.length > 0" class="workouts-wrapper">
          <div class="grid-cards">
            <CardWorkoutMinimal
              v-for="(item, i) in groupedWorkouts"
              :key="item._id + i"
              :workout="item"
            />
          </div>
        </div>
        <div
          v-if="groupedWorkouts.length === 0 && !isLoadingWorkouts"
          class="empty-message--large"
        >
          <h1
            v-if="
              groupedWorkouts.length === 0 && initialWorkoutsList.length === 0
            "
            >No workouts created yet. Get started by creating your first
            workout.</h1
          >
        </div>

        <div
          v-if="
            groupedWorkouts &&
              groupedWorkouts.length === 0 &&
              initialWorkoutsList &&
              initialWorkoutsList.length > 0 &&
              !isLoadingWorkouts
          "
          class="empty-state-container"
        >
          <h2>We couldn’t find what you’re looking for.</h2>
          <p
            >This workout does not exist in the workout library. If you want to
            use this workout, you will need create it.</p
          >
          <div class="actions">
            <BaseButton
              type="button"
              class="btn-secondary btn-round"
              @click="clearFilters"
            >
              Clear Filter
            </BaseButton>
            <BaseButton
              type="button"
              class="btn-primary btn-round"
              @click="showAdminPanel"
            >
              <img src="@assets/images/icon-powerlifter.svg" />
              Create New
            </BaseButton>
          </div>
        </div>

        <div class="loading-wrapper" v-if="isLoadingWorkouts">
          <BaseLoadingSpinner />
        </div>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.empty-state-container {
  background: $color-white;
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba($color-black-wo-rgb, 0.08);
  display: flex;
  flex-direction: column;
  margin-bottom: 30vh;
  margin-left: auto;
  margin-right: auto;
  margin-top: 25vh;
  padding: 6rem;
  text-align: center;
  width: 55%;
  p {
    margin-top: 2rem;
  }
  .actions {
    align-items: center;
    display: flex;
    justify-content: center;
    margin-top: 2rem;
    ::v-deep .button {
      height: 3.7rem;
      padding-bottom: 0.2rem;
      padding-top: 0.2rem;
      white-space: nowrap;
    }
    ::v-deep .button.btn-secondary {
      margin-right: 10px;
    }
  }
}
.loading-wrapper {
  align-items: center;
  display: flex;
  justify-content: center;
  margin-bottom: 30vh;
  margin-top: 35vh;
  width: 100%;
  ::v-deep .loading {
    height: 4rem;
    width: 4rem;
  }
}

.input-with-label .input {
  background-color: white;
}

.grid-cards {
  @include grid-cards(3, 30);
}
</style>
